var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var _a;
import _get from 'lodash/get';
import { action, computed, observable, makeObservable } from 'mobx';
import AuthAPI from '../services/AuthAPI';
import ProfileAPI from '../services/ProfileAPI';
import { checkTokenExpire } from '../framework/utils/FirebaseUtils';
import { WRONG_PASSWORD } from '../framework/constants/app';
import { EmailAuthProvider, auth, tokenUpdateAffect } from '../services/firebase/firebase';
import DealerProfileStore from './DealerProfileStore';
import { IDealerProfileType } from '../types/DealerProfileTypes';
import NotificationStore from './NotificationStore';
import ProfileStore from './ProfileStore';
import FinancingStore from './FinancingStore';
import VehicleDetailsStore from './VehicleDetailsStore';
import UserActionsContactAPI from 'services/UserActionsContactAPI';
const DEFAULT_AUTH_ERROR = {
    code: '',
    message: '',
    hasError: false
};
class AuthStore {
    constructor() {
        Object.defineProperty(this, "firebaseUser", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {}
        });
        Object.defineProperty(this, "errors", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {
                login: DEFAULT_AUTH_ERROR,
                register: DEFAULT_AUTH_ERROR,
                reset: DEFAULT_AUTH_ERROR
            }
        });
        Object.defineProperty(this, "isResetDone", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "isPasswordChanged", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        // By default this flag is set to true
        // just because we need to show loader during page load
        // if user directly goes to his private page.
        Object.defineProperty(this, "isAuthFetching", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: true
        });
        Object.defineProperty(this, "isSaleRequest", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "checkoutData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {}
        });
        Object.defineProperty(this, "shouldLoadCars", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "isTokenAlive", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => new Promise((resolve) => {
                if (this.isAuthorized) {
                    const isTokenExpire = checkTokenExpire(this.firebaseUser.exp);
                    if (isTokenExpire) {
                        this.updateToken(true).then(() => resolve());
                    }
                    resolve();
                }
                resolve();
            })
        });
        Object.defineProperty(this, "getIdToken", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                return auth.currentUser.getIdToken();
            }
        });
        Object.defineProperty(this, "getAlternative", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                return window.location.pathname.startsWith('/alternative') &&
                    this.isAuthorized
                    && (!!this.firebaseUser.isDadminS || !!this.firebaseUser.isSale);
            }
        });
        Object.defineProperty(this, "setOfferRequestForCustomer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                if (this.isAuthorized && !!this.firebaseUser.isDadminS) {
                    this.isSaleRequest = true;
                }
                else {
                    this.isSaleRequest = false;
                }
            }
        });
        Object.defineProperty(this, "updateToken", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (forceUpdate) => {
                return AuthAPI.updateToken(forceUpdate)
                    .then((token) => {
                    tokenUpdateAffect(token);
                });
            }
        });
        Object.defineProperty(this, "signUp", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (email, password) => {
                this.isAuthFetching = true;
                return AuthAPI.signUp(email, password)
                    .then(() => {
                    // new user created successfully
                    this.clearPagesState('register');
                })
                    .catch((error) => {
                    this.errors.register = { ...error, hasError: true };
                    this.isAuthFetching = false;
                    // Throw error higher into the code tree
                    // to be able to catch it in mobx sections
                    throw new Error(error);
                });
            }
        });
        Object.defineProperty(this, "signIn", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (email, password, t) => {
                this.isAuthFetching = true;
                return AuthAPI.signIn(email, password)
                    .then(() => {
                    this.clearPagesState('login');
                })
                    .catch((error) => {
                    this.errors.login = { ...error, hasError: true };
                    this.isAuthFetching = false;
                    if (error.code === WRONG_PASSWORD) {
                        NotificationStore.addNotification({
                            message: t('AuthRegisterPage:errorMessages.auth/wrong-password'),
                            level: 'error'
                        });
                    }
                    // Throw error higher into the code tree
                    // to be able to catch it in mobx sections
                    throw new Error(error);
                });
            }
        });
        Object.defineProperty(this, "signOut", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                return AuthAPI.signOut()
                    .then(() => {
                    this.clearUserState();
                    DealerProfileStore.clearUserList();
                });
            }
        });
        Object.defineProperty(this, "resetPassword", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (email) => {
                return AuthAPI.resetPassword(email)
                    .then(() => {
                    this.isResetDone = true;
                })
                    .catch((error) => {
                    this.errors.reset = { ...error, hasError: true };
                    throw new Error(error);
                });
            }
        });
        Object.defineProperty(this, "createNewPassword", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (code, newPassword) => {
                return AuthAPI.createNewPassword(code, newPassword)
                    .then(() => {
                    this.isPasswordChanged = true;
                })
                    .catch((error) => {
                    this.errors.reset = { ...error, hasError: true };
                });
            }
        });
        Object.defineProperty(this, "changePassword", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (oldPass, newPass) => {
                return ProfileAPI.changePassword(oldPass, newPass);
            }
        });
        Object.defineProperty(this, "changeEmail", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (newEmail) => {
                return ProfileAPI.changeEmail(newEmail);
            }
        });
        Object.defineProperty(this, "authenticate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (email, password) => {
                const credentials = EmailAuthProvider.credential(email, password);
                return AuthAPI.authenticate(credentials);
            }
        });
        Object.defineProperty(this, "clearPagesState", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (page) => {
                if (this.errors[page]) {
                    this.errors[page] = DEFAULT_AUTH_ERROR;
                }
                // special case for password reset page
                // clear the flag to reset the state of the page
                if (page === 'reset') {
                    this.isResetDone = false;
                }
            }
        });
        Object.defineProperty(this, "setRequestedCheckoutStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (carId) => {
                try {
                    const data = await UserActionsContactAPI.getCheckoutCar(carId);
                    if (data && data.car)
                        FinancingStore.setRequestedOffer(carId); // set requested car id
                    if (data && data.pathname)
                        FinancingStore.setCheckoutList(carId); // set checkout car idsetRequestedCheckoutStore
                    this.checkoutData = data;
                }
                catch (err) {
                    console.log('[setRequestedCheckoutStore] err = ', err);
                }
            }
        });
        Object.defineProperty(this, "updateUserState", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (userObject) => {
                this.firebaseUser = userObject;
                this.isAuthFetching = false;
                if (!!Object.keys(this.firebaseUser).length) {
                    const carId = _get(VehicleDetailsStore, 'car._id', '');
                    const typeAlternative = _get(VehicleDetailsStore, 'car.metaData.type', '');
                    // get requested and checkout data after auth is fetched
                    ProfileStore.getRequestedCarsCount();
                    if (carId && typeAlternative !== 'alternative')
                        this.setRequestedCheckoutStore(carId);
                }
            }
        });
        Object.defineProperty(this, "clearUserState", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.firebaseUser = {};
                this.isAuthFetching = false;
            }
        });
        Object.defineProperty(this, "setShouldLoadCars", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (state) => {
                this.shouldLoadCars = state;
            }
        });
        makeObservable(this);
    }
    get isAuthorized() { return !!Object.keys(this.firebaseUser).length; }
    get userEmail() { return this.isAuthorized ? this.firebaseUser.email : null; }
    get userId() { return this.isAuthorized ? this.firebaseUser.user_id : null; }
    get mainDealerId() {
        if (this.isAuthorized) {
            if (_get(this.firebaseUser, 'dealer_id'))
                return this.firebaseUser.dealer_id;
            if (_get(this.firebaseUser, 'isDadmin'))
                return this.firebaseUser.user_id;
        }
        else {
            return null;
        }
    }
    get isEmployee() {
        if (this.isAuthorized) {
            if (_get(this.firebaseUser, 'dealer_id') && _get(this.firebaseUser, 'isDadmin'))
                return true;
        }
        else {
            return false;
        }
    }
    get isDealerSuperAdmin() {
        return this.isAuthorized && !!this.firebaseUser.isDadminS;
    }
    get isSale() {
        const { location: { pathname } } = window;
        return !pathname.startsWith('/alternative') && this.isAuthorized && !!this.firebaseUser.isSale;
    }
    get isAlternative() {
        const { location: { pathname } } = window;
        return (pathname.startsWith('/alternative') &&
            this.isAuthorized
            && (!!this.firebaseUser.isDadminS || !!this.firebaseUser.isSale));
    }
    // flag that describes whether user a dealer or not
    get isDealer() {
        return !!this.mainDealerId;
    }
    // user roles
    get isDealerMain() {
        const { dealer_id, isDadmin } = this.firebaseUser;
        return isDadmin && !dealer_id;
    }
    get isDealerAdmin() {
        const { dealer_id, isDadmin } = this.firebaseUser;
        return dealer_id && isDadmin;
    }
    get isDealerAccountant() {
        const { dealer_id, isDacc } = this.firebaseUser;
        return dealer_id && isDacc;
    }
    get isDealerEmployee() {
        const { dealer_id, isDacc, isDadmin } = this.firebaseUser;
        return dealer_id && !isDacc && !isDadmin;
    }
    get isPurchase() {
        const { isPurchase } = this.firebaseUser;
        return isPurchase;
    }
    dealerSignUp(data) {
        this.isAuthFetching = true;
        return ProfileAPI.createDealerProfile(data)
            .then(() => AuthAPI.signIn(data.email, data.password))
            .finally(() => {
            this.isAuthFetching = false;
        });
    }
}
__decorate([
    observable,
    __metadata("design:type", Object)
], AuthStore.prototype, "firebaseUser", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AuthStore.prototype, "errors", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AuthStore.prototype, "isResetDone", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AuthStore.prototype, "isPasswordChanged", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AuthStore.prototype, "isAuthFetching", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AuthStore.prototype, "isSaleRequest", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AuthStore.prototype, "checkoutData", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AuthStore.prototype, "shouldLoadCars", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthStore.prototype, "isAuthorized", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthStore.prototype, "userEmail", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthStore.prototype, "userId", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthStore.prototype, "mainDealerId", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthStore.prototype, "isEmployee", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthStore.prototype, "isDealerSuperAdmin", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthStore.prototype, "isSale", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthStore.prototype, "isAlternative", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthStore.prototype, "isDealer", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthStore.prototype, "isDealerMain", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthStore.prototype, "isDealerAdmin", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthStore.prototype, "isDealerAccountant", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthStore.prototype, "isDealerEmployee", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthStore.prototype, "isPurchase", null);
__decorate([
    action,
    __metadata("design:type", Object)
], AuthStore.prototype, "isTokenAlive", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], AuthStore.prototype, "getIdToken", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], AuthStore.prototype, "getAlternative", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], AuthStore.prototype, "setOfferRequestForCustomer", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], AuthStore.prototype, "updateToken", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], AuthStore.prototype, "signUp", void 0);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [typeof (_a = typeof IDealerProfileType !== "undefined" && IDealerProfileType) === "function" ? _a : Object]),
    __metadata("design:returntype", void 0)
], AuthStore.prototype, "dealerSignUp", null);
__decorate([
    action,
    __metadata("design:type", Object)
], AuthStore.prototype, "signIn", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], AuthStore.prototype, "signOut", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], AuthStore.prototype, "resetPassword", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], AuthStore.prototype, "createNewPassword", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], AuthStore.prototype, "changePassword", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], AuthStore.prototype, "changeEmail", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], AuthStore.prototype, "authenticate", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], AuthStore.prototype, "clearPagesState", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], AuthStore.prototype, "setRequestedCheckoutStore", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], AuthStore.prototype, "updateUserState", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], AuthStore.prototype, "clearUserState", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], AuthStore.prototype, "setShouldLoadCars", void 0);
export default new AuthStore();
